import { forwardRef, useImperativeHandle } from 'react';
import {
  Col,
  Divider,
  Input,
  InputNumber,
  Row,
  Select,
  Typography,
} from 'antd';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import {
  CustomNotification,
  Form,
  SectionWrapper,
  WrapperSelect,
} from 'common/components';

import Nutrients from './components/Nutrients';
import { PET_FOOD_INITIAL_FORM } from '../../constants';
import { getPetFoodPanels } from '../../../multiple-panel/facts-panel/useQueryFactsPanel';
import { getKeyQaSpecPetFoodPanels } from 'pages/qa-spec/components/qa-nutrition-data/hook';
import { usePetFoodFactsLabel } from './hook';
import { useGetEnumsByEnumName } from 'hooks';
import { useFactPanelQasRequiredFields } from '../../hook/useFactPanelQasRequiredFields';
import { QaSpecHelpContentLabel } from 'pages/qa-spec/components/shared/QaSpecHelpContent';
import { TOOlTIP_FIELDS } from 'static/tooltipFields';

import './PetNutritionLabel.less';
import TraceableCommentContainer from 'pages/traceable-comment/TraceableCommentContainer';
import { getFormattedPath } from 'pages/qa-spec/components/shared/QaSpecHelper';

const labelTypeOptions = [
  {
    label: 'Standard',
    value: 'US_FDA_NFP_2020_STANDARD',
  },
];

const PetNutritionLabel = forwardRef((props, forwardedRef) => {
  const { id } = useParams();
  const [form] = Form.useForm();

  const { visible, factsPanel, typeView, onSubmitFail, onSubmitSuccess } =
    props;

  const queryClient = useQueryClient();

  const mutation = usePetFoodFactsLabel({ typeView });

  const { getRequiredRule } = useFactPanelQasRequiredFields({
    productId: id,
    typeView,
    section: 'qaSpecification.factsPanel.petNutritionLabel',
  });

  const isQaSpecType = typeView === 'qa-spec';

  const submitForm = async () => {
    //const formValues = await form.validateFields();
    const formValues = form.getFieldsValue(true);

    if (!formValues) return;

    const params = {
      productItemId: +id,
      data: {
        id: factsPanel?.id || 0,
        ...formValues,
      },
      deletedFields: [],
    };

    mutation.mutate(params, {
      onSuccess: (result) => {
        if (result.isSuccess) {
          resetForm();
          onSubmitSuccess && onSubmitSuccess();

          queryClient.invalidateQueries({
            queryKey: getPetFoodPanels(id),
          });
          queryClient.invalidateQueries({
            queryKey: getKeyQaSpecPetFoodPanels(id),
          });
          CustomNotification.success(
            'Update Pet Food Nutrition Facts successfully'
          );
        } else {
          form.resetFields();

          onSubmitFail && onSubmitFail();

          CustomNotification.error('Something went wrong!');
        }
      },
    });
  };

  const initForm = (on) => {
    const initialData = on === 'add' ? PET_FOOD_INITIAL_FORM : factsPanel;
    form.setFieldsValue({
      ...initialData,
    });
  };

  const resetForm = () => {
    form.resetFields();
  };

  useImperativeHandle(forwardedRef, () => {
    return {
      submitForm,
      initForm,
      resetForm,
    };
  });

  const petNutritionPerCupUnitEnum = useGetEnumsByEnumName(
    'PetNutritionPerCupUnitEnum'
  );

  return visible ? (
    <Form
      form={form}
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
      className='pet-nutrition-form'
    >
      <Row wrap={false} gutter={16} style={{ height: '100%' }}>
        <Col span={8}>
          <Form.Item
            name='nutrientFormatType'
            label={
              <TraceableCommentContainer
                qaSpecName={getFormattedPath(
                  TOOlTIP_FIELDS.petNutritionLabelType.fieldPath
                )}
                qaSpecFieldName='Label Type'
              >
                <QaSpecHelpContentLabel
                  fieldPath={TOOlTIP_FIELDS.petNutritionLabelType.fieldPath}
                  isEnabled={isQaSpecType}
                >
                  Label Type
                </QaSpecHelpContentLabel>
              </TraceableCommentContainer>
            }
            rules={[
              getRequiredRule({
                fieldName: ['nutrientFormatType'],
                displayName: 'Label Type',
              }),
            ]}
          >
            <Select
              allowClear
              defaultValue={labelTypeOptions}
              options={labelTypeOptions}
              getPopupContainer={(triggerNode) => {
                return triggerNode;
              }}
            />
          </Form.Item>
          <Row gutter={4}>
            <Col span={12}>
              <Form.Item
                name='measurementPerCup'
                label={
                  <TraceableCommentContainer
                    qaSpecName={getFormattedPath(
                      TOOlTIP_FIELDS.petNutritionMeasurementPerCup.fieldPath
                    )}
                    qaSpecFieldName='Per Cup Measurement'
                  >
                    <QaSpecHelpContentLabel
                      fieldPath={
                        TOOlTIP_FIELDS.petNutritionMeasurementPerCup.fieldPath
                      }
                      isEnabled={isQaSpecType}
                    >
                      Per Cup Measurement
                    </QaSpecHelpContentLabel>
                  </TraceableCommentContainer>
                }
                rules={[
                  {
                    required: true,
                    message: 'Per Cup Measurement is required',
                  },
                ]}
              >
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name='measurementUomPerCup'
                label={
                  <TraceableCommentContainer
                    qaSpecName={getFormattedPath(
                      TOOlTIP_FIELDS.petNutritionMeasurementUomPerCup.fieldPath
                    )}
                    qaSpecFieldName='Per Cup Measurement Unit'
                  >
                    <QaSpecHelpContentLabel
                      fieldPath={
                        TOOlTIP_FIELDS.petNutritionMeasurementUomPerCup
                          .fieldPath
                      }
                      isEnabled={isQaSpecType}
                    >
                      Per Cup Measurement Unit
                    </QaSpecHelpContentLabel>
                  </TraceableCommentContainer>
                }
                rules={[
                  {
                    required: true,
                    message: 'Per Cup Measurement Unit is required',
                  },
                ]}
              >
                <WrapperSelect
                  placeholder='Unit'
                  getPopupContainer={(triggerNode) => {
                    return triggerNode;
                  }}
                  allowClear={true}
                >
                  {[
                    petNutritionPerCupUnitEnum?.map((labelType) => {
                      return (
                        <Select.Option
                          value={labelType?.enumDisplayName}
                          key={labelType?.enumDisplayName}
                        >
                          {labelType?.enumDisplayName}
                        </Select.Option>
                      );
                    }),
                  ]}
                </WrapperSelect>
              </Form.Item>
            </Col>
          </Row>
          <Divider style={{ margin: '8px 0' }} />
          <SectionWrapper title='Calories Content'>
            <Row>
              <Col span={24}>
                <Row gutter={4}>
                  <Col span={12}>
                    <Form.Item
                      name='calorieContent'
                      label={
                        <TraceableCommentContainer
                          qaSpecName={getFormattedPath(
                            TOOlTIP_FIELDS.petNutritionCalorieContent.fieldPath
                          )}
                          qaSpecFieldName='Calories Content'
                        >
                          <QaSpecHelpContentLabel
                            fieldPath={
                              TOOlTIP_FIELDS.petNutritionCalorieContent
                                .fieldPath
                            }
                            isEnabled={isQaSpecType}
                          >
                            Calories Content
                          </QaSpecHelpContentLabel>
                        </TraceableCommentContainer>
                      }
                      rules={[
                        getRequiredRule({
                          fieldName: ['caloriesContent', 'calorieContent'],
                          displayName: 'Calories Content',
                        }),
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name='caloriesPerCup'
                      label={
                        <TraceableCommentContainer
                          qaSpecName={getFormattedPath(
                            TOOlTIP_FIELDS.petNutritionCaloriesPerCup.fieldPath
                          )}
                          qaSpecFieldName='Calories Per Cup'
                        >
                          <QaSpecHelpContentLabel
                            fieldPath={
                              TOOlTIP_FIELDS.petNutritionCaloriesPerCup
                                .fieldPath
                            }
                            isEnabled={isQaSpecType}
                          >
                            Calories Per Cup
                          </QaSpecHelpContentLabel>
                        </TraceableCommentContainer>
                      }
                      rules={[
                        getRequiredRule({
                          fieldName: ['caloriesContent', 'caloriesPerCup'],
                          displayName: 'Calories Per Cup',
                        }),
                      ]}
                    >
                      <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>
                <p>From</p>
                <Form.Item
                  name='protein'
                  label={
                    <TraceableCommentContainer
                      qaSpecName={getFormattedPath(
                        TOOlTIP_FIELDS.petNutritionProtein.fieldPath
                      )}
                      qaSpecFieldName='Protein'
                    >
                      <QaSpecHelpContentLabel
                        fieldPath={TOOlTIP_FIELDS.petNutritionProtein.fieldPath}
                        isEnabled={isQaSpecType}
                      >
                        Protein
                      </QaSpecHelpContentLabel>
                    </TraceableCommentContainer>
                  }
                  labelCol={{ span: 8 }}
                  wrapperCol={{ span: 16 }}
                  rules={[
                    getRequiredRule({
                      fieldName: ['caloriesContent', 'protein'],
                      displayName: 'From Protein',
                    }),
                  ]}
                >
                  <InputNumber style={{ width: '100%' }} min={0} />
                </Form.Item>
                <Form.Item
                  name='fat'
                  label={
                    <TraceableCommentContainer
                      qaSpecName={getFormattedPath(
                        TOOlTIP_FIELDS.petNutritionFat.fieldPath
                      )}
                      qaSpecFieldName='Fat'
                    >
                      <QaSpecHelpContentLabel
                        fieldPath={TOOlTIP_FIELDS.petNutritionFat.fieldPath}
                        isEnabled={isQaSpecType}
                      >
                        Fat
                      </QaSpecHelpContentLabel>
                    </TraceableCommentContainer>
                  }
                  labelCol={{ span: 8 }}
                  wrapperCol={{ span: 16 }}
                  rules={[
                    getRequiredRule({
                      fieldName: ['caloriesContent', 'fat'],
                      displayName: 'From Fat',
                    }),
                  ]}
                >
                  <InputNumber style={{ width: '100%' }} min={0} />
                </Form.Item>
                <Form.Item
                  name='carbohydrate'
                  label={
                    <TraceableCommentContainer
                      qaSpecName={getFormattedPath(
                        TOOlTIP_FIELDS.petNutritionCarbohydrate.fieldPath
                      )}
                      qaSpecFieldName='Carbohydrate'
                    >
                      <QaSpecHelpContentLabel
                        fieldPath={
                          TOOlTIP_FIELDS.petNutritionCarbohydrate.fieldPath
                        }
                        isEnabled={isQaSpecType}
                      >
                        Carbohydrate
                      </QaSpecHelpContentLabel>
                    </TraceableCommentContainer>
                  }
                  labelCol={{ span: 8 }}
                  wrapperCol={{ span: 16 }}
                  rules={[
                    getRequiredRule({
                      fieldName: ['caloriesContent', 'carbohydrate'],
                      displayName: 'From Carbohydrate',
                    }),
                  ]}
                >
                  <InputNumber style={{ width: '100%' }} min={0} />
                </Form.Item>
              </Col>
            </Row>
          </SectionWrapper>
          <Divider style={{ margin: '16px 0' }} />
          <Form.Item label='Special Character' style={{ display: 'flex' }}>
            <Typography.Paragraph
              copyable={{
                text: '*',
              }}
            >
              * Not recognized as an essential nutrient by the AAFCO
            </Typography.Paragraph>
            <Typography.Paragraph copyable={{ text: '†' }}>
              † Calculated Value
            </Typography.Paragraph>
          </Form.Item>
        </Col>

        <Col span={8}>
          <SectionWrapper
            title={
              <>
                <TraceableCommentContainer
                  qaSpecName={'Fact Panels Pet Nutritional Information'}
                  qaSpecFieldName='Nutrients'
                >
                  Nutrients
                </TraceableCommentContainer>
              </>
            }
          >
            <Nutrients productId={id} typeView={typeView} />
          </SectionWrapper>
        </Col>
        <Col span={8}>
          <Form.Item
            name='fdaLabelFootNote'
            label={
              <TraceableCommentContainer
                qaSpecName={getFormattedPath(
                  TOOlTIP_FIELDS.petNutritionFdaLabelFootNote.fieldPath
                )}
                qaSpecFieldName='Footer Note'
              >
                <QaSpecHelpContentLabel
                  fieldPath={
                    TOOlTIP_FIELDS.petNutritionFdaLabelFootNote.fieldPath
                  }
                  isEnabled={isQaSpecType}
                >
                  Footer Note
                </QaSpecHelpContentLabel>
              </TraceableCommentContainer>
            }
            rules={[
              getRequiredRule({
                fieldName: ['fdaLabelFootNote'],
                displayName: 'Footer Note',
              }),
            ]}
          >
            <Input.TextArea rows={5} maxLength={300} />
          </Form.Item>
          <Form.Item
            name='enzymesAndMicroorganisms'
            label={
              <TraceableCommentContainer
                qaSpecName={getFormattedPath(
                  TOOlTIP_FIELDS.petNutritionEnzymesAndMicroorganisms.fieldPath
                )}
                qaSpecFieldName='Enzymes And Microorganisms'
              >
                <QaSpecHelpContentLabel
                  fieldPath={
                    TOOlTIP_FIELDS.petNutritionEnzymesAndMicroorganisms
                      .fieldPath
                  }
                  isEnabled={isQaSpecType}
                >
                  Enzymes And Microorganisms
                </QaSpecHelpContentLabel>
              </TraceableCommentContainer>
            }
            rules={[
              getRequiredRule({
                fieldName: ['enzymesAndMicroorganisms'],
                displayName: 'Enzymes And Microorganisms',
              }),
            ]}
          >
            <Input.TextArea rows={5} maxLength={300} />
          </Form.Item>
          <Form.Item
            name='nutritionalAdequacyStatement'
            label={
              <TraceableCommentContainer
                qaSpecName={getFormattedPath(
                  TOOlTIP_FIELDS.petNutritionNutritionalAdequacyStatement
                    .fieldPath
                )}
                qaSpecFieldName='Nutritional Adequacy Statement'
              >
                <QaSpecHelpContentLabel
                  fieldPath={
                    TOOlTIP_FIELDS.petNutritionNutritionalAdequacyStatement
                      .fieldPath
                  }
                  isEnabled={isQaSpecType}
                >
                  Nutritional Adequacy Statement
                </QaSpecHelpContentLabel>
              </TraceableCommentContainer>
            }
            rules={[
              getRequiredRule({
                fieldName: ['nutritionalAdequacyStatement'],
                displayName: 'Nutritional Adequacy Statement',
              }),
            ]}
          >
            <Input.TextArea rows={5} maxLength={300} />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  ) : null;
});

export default PetNutritionLabel;
